/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { ChangeEvent, FC, KeyboardEvent, useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { FinancialCalcReversalReq } from '../api/selfApi'
import '../assets/css/libs.css'
import '../assets/css/style.css'
import { fixedNumber } from '../js/utils'
import { constraintValues, defaultValues, FormInput } from './FinancialCalcReversal'
import {
  fetchFinancialCalcReversal,
  resetResponseCode,
} from '../store/modules/financialCalcReversal'
import { FinancialCalcReversalCalcButton } from './FinancialCalcReversalCalcButton'
import { AppState } from '../store'

interface FinancialCalcReversalFormProps {
  isPending: boolean
  isResponsive: boolean
  setIsMonthlyWithdrawal: React.Dispatch<React.SetStateAction<boolean>>
  setDisSavingRate: React.Dispatch<React.SetStateAction<number | string | null>>
  setAddition: React.Dispatch<React.SetStateAction<number | string | null>>
}

type FinancialCalcReversalFormInputNumberName =
  | 'initial'
  | 'startAge'
  | 'interest'
  | 'disSavingRate'
  | 'addition'

function getDecimalPointLength(value: string) {
  if (isNaN(Number(value))) {
    return NaN
  }
  const numberParts = value.split('.')
  return numberParts[1] ? numberParts[1].length : 0
}

function makeRequest(arg: FormInput): FinancialCalcReversalReq {
  return {
    initial: Number(arg.initial) * 10000,
    startAge: Number(arg.startAge),
    interest: Number(arg.interest),
    isMonthlyWithdrawal: arg.isMonthlyWithdrawal === 'true',
    disSavingRate: arg.disSavingRate === '' ? null : Number(arg.disSavingRate),
    addition: arg.addition === '' ? null : Number(arg.addition) * 10000,
  } as FinancialCalcReversalReq
}

export const FinancialCalcReversalForm: FC<FinancialCalcReversalFormProps> = React.memo(
  ({
    isPending,
    isResponsive,
    setIsMonthlyWithdrawal,
    setDisSavingRate,
    setAddition,
    children,
  }) => {
    const dispatch = useDispatch()
    const { register, handleSubmit, setValue, getValues } = useForm<FormInput>({
      mode: 'onSubmit',
    })

    // 各フィールドのエラーメッセージ
    const [initialError, setInitialError] = React.useState<string>('')
    const [startAgeError, setStartAgeError] = React.useState<string>('')
    const [interestError, setInterestError] = React.useState<string>('')
    const [disSavingRateError, setDisSavingRateError] = React.useState<string>('')
    const [additionError, setAdditionError] = React.useState<string>('')
    const [disSavingRateAndAdditionError, setDisSavingRateAndAdditionError] =
      React.useState<string>('')
    // サーバーエラーメッセージ（responseCode由来）
    const [serverError, setServerError] = React.useState<string>('')

    const { responseCode } = useSelector((state: AppState) => state.financialCalcReversal)

    useEffect(() => {
      if (responseCode === 1) {
        setServerError('計算結果が上限を超えています。使い方ガイドを確認してください。')
      } else if (responseCode !== 0) {
        setServerError('サーバーエラーが発生しました。改めて入力をお願いいたします。')
      } else {
        setServerError('')
      }
    }, [responseCode])

    const onSubmit = useCallback(
      (arg: FormInput) => {
        // Enter押下時など、onBlurが発火しない場合に備えて事前に整形
        const normalizeValue = (
          value: any,
          decimalPoint: number,
          upper: number,
          lower: number
        ): number | string => {
          if (value === '' || value === null || value === undefined) {
            return ''
          }

          const numValue = Number(value)
          // 異常値チェック（範囲外なら即return）
          if (isNaN(numValue) || numValue > upper || numValue < lower) {
            return value
          }

          // 0の正規化
          if (numValue === 0) return 0

          // 小数点桁数に応じて整形
          const valueStr = value.toString()
          const inputDecimalLength = getDecimalPointLength(valueStr)

          if (inputDecimalLength > 0 || valueStr.includes('.')) {
            // 小数点がある場合：指定桁数に整形（15.00 → 15.0、06.0 → 6.0）
            const integerPart = Number(valueStr.split('.')[0])
            const decimalPart = valueStr.split('.')[1]
            // 小数点以下が0のときだけ整形
            if (Number(decimalPart) === 0) {
              if (decimalPart.length <= decimalPoint) {
                // 入力桁数が指定桁数以下の場合は先頭0を除去してセット(06.0 → 6.0)
                return `${integerPart}.${decimalPart}`
              } else if (decimalPoint === 0) {
                // 指定桁数が0の場合は整数部のみセット（15.00 → 15）
                return integerPart
              } else {
                // 指定桁数以上の場合は桁数に合わせて整形してセット（06.00 → 6.0）
                return `${integerPart}.${decimalPart.slice(0, decimalPoint)}`
              }
            }
          }

          return numValue
        }

        // 各フィールドを整形
        const normalizedInitial = normalizeValue(
          arg.initial,
          constraintValues.initial.decimalPoint,
          constraintValues.initial.upper,
          constraintValues.initial.lower
        )
        const normalizedStartAge = normalizeValue(
          arg.startAge,
          constraintValues.startAge.decimalPoint,
          constraintValues.startAge.upper,
          constraintValues.startAge.lower
        )
        const normalizedInterest = normalizeValue(
          arg.interest,
          constraintValues.interest.decimalPoint,
          constraintValues.interest.upper,
          constraintValues.interest.lower
        )
        const normalizedDisSavingRate = normalizeValue(
          arg.disSavingRate,
          constraintValues.disSavingRate.decimalPoint,
          constraintValues.disSavingRate.upper,
          constraintValues.disSavingRate.lower
        )
        const normalizedAddition = normalizeValue(
          arg.addition,
          constraintValues.addition.decimalPoint,
          constraintValues.addition.upper,
          constraintValues.addition.lower
        )

        // 整形後の値をフォームにセット
        if (normalizedInitial !== '') {
          setValue('initial', normalizedInitial, { shouldValidate: false })
        }
        if (normalizedStartAge !== '') {
          setValue('startAge', normalizedStartAge, { shouldValidate: false })
        }
        if (normalizedInterest !== '') {
          setValue('interest', normalizedInterest, { shouldValidate: false })
        }
        if (normalizedDisSavingRate !== '') {
          setValue('disSavingRate', normalizedDisSavingRate, {
            shouldValidate: false,
          })
        }
        if (normalizedAddition !== '') {
          setValue('addition', normalizedAddition, { shouldValidate: false })
        }

        // 整形後の値を使用してバリデーション
        arg.initial = normalizedInitial
        arg.startAge = normalizedStartAge
        arg.interest = normalizedInterest
        arg.disSavingRate = normalizedDisSavingRate
        arg.addition = normalizedAddition

        // responseCodeを0にリセット
        dispatch(resetResponseCode())

        let hasError = false

        // initial のバリデーション
        if (arg.initial === '' || arg.initial === null) {
          setInitialError('1～99999の範囲にて、整数のみ有効です。半角数字で入力してください。')
          hasError = true
        } else {
          const initialNum = Number(arg.initial)
          if (
            isNaN(initialNum) ||
            initialNum > constraintValues.initial.upper ||
            initialNum < constraintValues.initial.lower ||
            getDecimalPointLength(initialNum.toString()) > constraintValues.initial.decimalPoint ||
            String(arg.initial).toLowerCase().includes('e')
          ) {
            setInitialError('1～99999の範囲にて、整数のみ有効です。半角数字で入力してください。')
            hasError = true
          } else {
            setInitialError('')
          }
        }

        // startAge のバリデーション
        if (arg.startAge === '' || arg.startAge === null) {
          setStartAgeError('20～99の範囲にて、整数のみ有効です。半角数字で入力してください。')
          hasError = true
        } else {
          const startAgeNum = Number(arg.startAge)
          if (
            isNaN(startAgeNum) ||
            startAgeNum > constraintValues.startAge.upper ||
            startAgeNum < constraintValues.startAge.lower ||
            getDecimalPointLength(startAgeNum.toString()) >
              constraintValues.startAge.decimalPoint ||
            String(arg.startAge).toLowerCase().includes('e')
          ) {
            setStartAgeError('20～99の範囲にて、整数のみ有効です。半角数字で入力してください。')
            hasError = true
          } else {
            setStartAgeError('')
          }
        }

        // interest のバリデーション
        if (arg.interest === '' || arg.interest === null) {
          setInterestError(
            '0～30.0の範囲にて、小数点一桁まで有効です。半角数字で入力してください。'
          )
          hasError = true
        } else {
          const interestNum = Number(arg.interest)
          if (
            isNaN(interestNum) ||
            interestNum > constraintValues.interest.upper ||
            interestNum < constraintValues.interest.lower ||
            getDecimalPointLength(interestNum.toString()) >
              constraintValues.interest.decimalPoint ||
            String(arg.interest).toLowerCase().includes('e')
          ) {
            setInterestError(
              '0～30.0の範囲にて、小数点一桁まで有効です。半角数字で入力してください。'
            )
            hasError = true
          } else {
            setInterestError('')
          }
        }

        // disSavingRate のバリデーション（空文字許可）
        if (arg.disSavingRate !== '') {
          const disSavingRateNum = Number(arg.disSavingRate)
          if (
            isNaN(disSavingRateNum) ||
            disSavingRateNum > constraintValues.disSavingRate.upper ||
            disSavingRateNum < constraintValues.disSavingRate.lower ||
            getDecimalPointLength(disSavingRateNum.toString()) >
              constraintValues.disSavingRate.decimalPoint ||
            String(arg.disSavingRate).toLowerCase().includes('e')
          ) {
            setDisSavingRateError(
              '定率（年間）は0～15.0の範囲にて、小数点一桁まで有効です。半角数字で入力してください。'
            )
            hasError = true
          } else {
            setDisSavingRateError('')
          }
        } else {
          setDisSavingRateError('')
        }

        // addition のバリデーション（空文字許可）
        if (arg.addition !== '') {
          const additionNum = Number(arg.addition)
          if (
            isNaN(additionNum) ||
            additionNum > constraintValues.addition.upper ||
            additionNum < constraintValues.addition.lower ||
            getDecimalPointLength(additionNum.toString()) >
              constraintValues.addition.decimalPoint ||
            String(arg.addition).toLowerCase().includes('e')
          ) {
            setAdditionError(
              '定額（1回あたり）は0～999.9の範囲にて、小数点一桁まで有効です。半角数字で入力してください。'
            )
            hasError = true
          } else {
            setAdditionError('')
          }
        } else {
          setAdditionError('')
        }

        if (Number(arg.initial) < Number(arg.addition)) {
          setAdditionError(
            '定額（1回あたり）は取り崩し開始時点の保有資産より小さい値を設定してください。'
          )
          hasError = true
        }

        if (arg.disSavingRate === '' && arg.addition === '') {
          setDisSavingRateAndAdditionError('定率・定額のいずれかまたは両方に値を入力してください。')
          hasError = true
        } else {
          setDisSavingRateAndAdditionError('')
        }

        if (hasError) {
          setTimeout(() => {
            formRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            })
          }, 500)

          return
        }

        setIsMonthlyWithdrawal(arg.isMonthlyWithdrawal === 'true')
        setDisSavingRate(arg.disSavingRate === '' ? null : arg.disSavingRate)
        setAddition(arg.addition === '' ? null : arg.addition)
        const param = makeRequest(arg)
        dispatch(fetchFinancialCalcReversal(param))
      },
      [setValue, setIsMonthlyWithdrawal, setDisSavingRate, setAddition, dispatch]
    )

    const handleChange = useCallback(
      (
        e: ChangeEvent<HTMLInputElement>,
        upper: number,
        lower: number,
        decimalPoint: number,
        options?: { allowEmpty?: boolean }
      ) => {
        const inputValue = e.target.value
        const inputName = e.target.name as FinancialCalcReversalFormInputNumberName

        // 空文字チェック
        if (inputValue === '') {
          return
        }

        const numValue = Number(inputValue)

        // 異常値チェック（範囲外なら即return）
        if (isNaN(numValue) || numValue > upper || numValue < lower) {
          return
        }

        // 0の正規化（0, 0.0, 0.00 → 0）
        if (numValue === 0) {
          setValue(inputName, 0, {
            shouldValidate: true,
          })
          return
        }

        // 正常値の場合：小数点桁数に応じて適切な形式に整形
        const inputDecimalLength = getDecimalPointLength(inputValue)

        if (inputDecimalLength > 0 || inputValue.includes('.')) {
          // 小数点がある場合：指定桁数に整形（15.00 → 15.0、06.0 → 6.0）
          const integerPart = Number(inputValue.split('.')[0])
          const decimalPart = inputValue.split('.')[1]
          // 小数点以下が0のときだけ整形
          if (Number(decimalPart) === 0) {
            if (decimalPart.length <= decimalPoint) {
              // 入力桁数が指定桁数以下の場合は先頭0を除去してセット(06.0 → 6.0)
              setValue(inputName, `${integerPart}.${decimalPart}`, {
                shouldValidate: true,
              })
              return
            } else if (decimalPoint === 0) {
              // 指定桁数が0の場合は整数部のみセット（15.00 → 15）
              setValue(inputName, integerPart, {
                shouldValidate: true,
              })
              return
            } else {
              // 指定桁数以上の場合は桁数に合わせて整形してセット（06.00 → 6.0）
              setValue(inputName, `${integerPart}.${decimalPart.slice(0, decimalPoint)}`, {
                shouldValidate: true,
              })
              return
            }
          }
        }

        // // 先頭0の処理
        // if (inputValue.match(/^0/) !== null) {
        //   setValue(inputName, numValue, {
        //     shouldValidate: true,
        //   })
        // }
      },
      [setValue]
    )

    const handleUpDownKey = useCallback(
      (
        e: KeyboardEvent<HTMLInputElement>,
        inputName: FinancialCalcReversalFormInputNumberName,
        step: number,
        decimalPoint: number
      ) => {
        const value = getValues(inputName)
        const nowValue = value !== undefined ? Number(value) : Number(defaultValues[inputName])

        if (e.key === 'ArrowUp') {
          const calcValue = nowValue + step
          const newValue =
            calcValue <= constraintValues[inputName].upper
              ? Number(fixedNumber(calcValue, decimalPoint))
              : constraintValues[inputName].upper
          setValue(inputName, newValue, { shouldValidate: true })
          e.preventDefault()
          return
        }
        if (e.key === 'ArrowDown') {
          const calcValue = nowValue - step
          const newValue =
            calcValue >= constraintValues[inputName].lower
              ? Number(fixedNumber(calcValue, decimalPoint))
              : constraintValues[inputName].lower
          setValue(inputName, newValue, { shouldValidate: true })
          e.preventDefault()
          return
        }
      },
      [getValues, setValue]
    )

    // <input> 要素を1つ上でラップしている <div> 要素ごと抽出（最小の役割の単位）
    const InputInitial = useCallback(
      (props: { errorMessage?: string }) => {
        return (
          <div className="c-form-group">
            <span className="c-form-group__sup u-md-mt20px reversal_input_title">
              取り崩し開始時点の保有資産
            </span>
            <span className="c-form-group__input">
              <input
                className={`c-form__input u-text--right u-size--w140px${
                  props.errorMessage ? ' input_error' : ''
                }`}
                type="number"
                name="initial"
                step="any"
                defaultValue={defaultValues.initial}
                onKeyDown={(e) =>
                  handleUpDownKey(e, 'initial', 10, constraintValues.initial.decimalPoint)
                }
                onBlurCapture={(e) =>
                  handleChange(
                    e,
                    constraintValues.initial.upper,
                    constraintValues.initial.lower,
                    constraintValues.initial.decimalPoint
                  )
                }
                ref={register({ required: false })}
              />
              <span className="c-form-group__text">万円</span>
            </span>
            {props.errorMessage && <p className="error_message">{props.errorMessage}</p>}
          </div>
        )
      },
      [handleChange, handleUpDownKey, register]
    )

    const InputAddition = useCallback(
      (props: { errorMessage?: string }) => {
        return (
          <div className="c-form-group">
            <span className="c-form-group__sup notit">定額（1回あたり）</span>
            <span className="c-form-group__input">
              <input
                className={`c-form__input u-text--right u-size--w100px${
                  props.errorMessage ? ' input_error' : ''
                }`}
                type="number"
                name="addition"
                step="any"
                defaultValue={defaultValues.addition}
                onKeyDown={(e) =>
                  handleUpDownKey(e, 'addition', 0.1, constraintValues.addition.decimalPoint)
                }
                onBlurCapture={(e) =>
                  handleChange(
                    e,
                    constraintValues.addition.upper,
                    constraintValues.addition.lower,
                    constraintValues.addition.decimalPoint,
                    { allowEmpty: true }
                  )
                }
                ref={register({ required: false })}
              />
              <span className="c-form-group__text">万円</span>
            </span>
          </div>
        )
      },
      [handleChange, handleUpDownKey, register]
    )

    const InputInterest = useCallback(
      (props: { errorMessage?: string }) => {
        return (
          <div className="c-form-group">
            <span className="c-form-group__sup u-md-mt20px reversal_input_title">
              運用した場合のリターン(年率)
            </span>
            <span className="c-form-group__input">
              <input
                className={`c-form__input u-text--right u-size--w90px${
                  props.errorMessage ? ' input_error' : ''
                }`}
                type="number"
                name="interest"
                step="any"
                defaultValue={defaultValues.interest}
                onKeyDown={(e) =>
                  handleUpDownKey(e, 'interest', 0.1, constraintValues.interest.decimalPoint)
                }
                onBlurCapture={(e) =>
                  handleChange(
                    e,
                    constraintValues.interest.upper,
                    constraintValues.interest.lower,
                    constraintValues.interest.decimalPoint
                  )
                }
                ref={register({ required: false })}
              />
              <span className="c-form-group__text">%</span>
            </span>
            {props.errorMessage && <p className="error_message">{props.errorMessage}</p>}
          </div>
        )
      },
      [handleChange, handleUpDownKey, register]
    )

    const InputStartAge = useCallback(
      (props: { errorMessage?: string }) => {
        return (
          <div className="c-form-group">
            <span className="c-form-group__sup u-md-mt20px reversal_input_title">
              取り崩し開始年齢
            </span>
            <span className="c-form-group__input">
              <input
                className={`c-form__input u-text--right u-size--w140px${
                  props.errorMessage ? ' input_error' : ''
                }`}
                type="number"
                name="startAge"
                step="any"
                defaultValue={defaultValues.startAge}
                onKeyDown={(e) =>
                  handleUpDownKey(e, 'startAge', 1, constraintValues.startAge.decimalPoint)
                }
                onBlurCapture={(e) =>
                  handleChange(
                    e,
                    constraintValues.startAge.upper,
                    constraintValues.startAge.lower,
                    constraintValues.startAge.decimalPoint
                  )
                }
                ref={register({ required: false })}
              />
              <span className="c-form-group__text">歳</span>
            </span>
            {props.errorMessage && <p className="error_message">{props.errorMessage}</p>}
          </div>
        )
      },
      [handleChange, handleUpDownKey, register]
    )

    const InputIsMonthlyWithdrawal = useCallback(() => {
      return (
        <>
          <div className="c-form-group">
            <span className="c-form-group__sup u-md-mt20px reversal_input_title">取り崩し頻度</span>
            <p className="c-note c-note-reversal sp_pd_le0px">*どちらか一方をお選びください。</p>
            <div className="radio-pill u-mt10px">
              <label htmlFor="monthly">
                <input
                  type="radio"
                  id="monthly"
                  name="isMonthlyWithdrawal"
                  defaultChecked={defaultValues.isMonthlyWithdrawal}
                  ref={register}
                  value="true"
                />
                毎月
              </label>

              <label htmlFor="bimonthly">
                <input
                  type="radio"
                  id="bimonthly"
                  name="isMonthlyWithdrawal"
                  defaultChecked={!defaultValues.isMonthlyWithdrawal}
                  ref={register}
                  value="false"
                />
                隔月
              </label>
            </div>
          </div>
        </>
      )
    }, [register])

    const InputDisSavingRate = useCallback(
      (props: { errorMessage?: string }) => {
        return (
          <div className="c-form-group">
            <span className="c-form-group__sup notit">定率（年間）</span>
            <span className="c-form-group__input">
              <input
                className={`c-form__input u-text--right u-size--w100px${
                  props.errorMessage ? ' input_error' : ''
                }`}
                type="number"
                name="disSavingRate"
                step="any"
                defaultValue={defaultValues.disSavingRate}
                onKeyDown={(e) =>
                  handleUpDownKey(
                    e,
                    'disSavingRate',
                    0.1,
                    constraintValues.disSavingRate.decimalPoint
                  )
                }
                onBlurCapture={(e) =>
                  handleChange(
                    e,
                    constraintValues.disSavingRate.upper,
                    constraintValues.disSavingRate.lower,
                    constraintValues.disSavingRate.decimalPoint,
                    { allowEmpty: true }
                  )
                }
                ref={register({ required: false })}
              />
              <span className="c-form-group__text">%</span>
            </span>
          </div>
        )
      },
      [handleChange, handleUpDownKey, register]
    )

    const data = useSelector((state: AppState) => state.financialCalcReversal)
    const formRef = React.useRef<HTMLFormElement | null>(null)

    useEffect(() => {
      if (responseCode !== 0) {
        setTimeout(() => {
          formRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
        }, 500)
      }
    }, [data, responseCode])

    return (
      <React.Fragment>
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off" ref={formRef}>
          {serverError && <p className="form_error_message">{serverError}</p>}
          <div className="p-simulation__grid-3 u-mb30px">
            <div className="p-simulation__grid-3__col u-size--w350px">
              <InputInitial errorMessage={initialError} />
              <InputStartAge errorMessage={startAgeError} />
            </div>
            <div className="p-simulation__grid-3__col u-size--w450px">
              <div className="c-grid__col">
                <InputIsMonthlyWithdrawal />
              </div>
              <p className="c-form-group__sup u-md-mt20px reversal_input_title">取り崩し方法</p>
              <p className="c-note c-note-reversal sp_pd_le15px">
                *いずれかまたは両方にご入力ください。
              </p>
              <div className="c-grid reversal_ways_input">
                <div className="c-grid__col">
                  <InputDisSavingRate
                    errorMessage={disSavingRateError || disSavingRateAndAdditionError}
                  />
                </div>
                <div className="c-grid__col">
                  <InputAddition errorMessage={additionError || disSavingRateAndAdditionError} />
                </div>
              </div>
              {disSavingRateError && (
                <p className="error_message sp_pd_le15px sp_pd_ri15px">{disSavingRateError}</p>
              )}
              {additionError && (
                <p className="error_message sp_pd_le15px sp_pd_ri15px">{additionError}</p>
              )}
              {disSavingRateAndAdditionError && (
                <p className="error_message sp_pd_le15px sp_pd_ri15px">
                  {disSavingRateAndAdditionError}
                </p>
              )}
            </div>
            <div className="p-simulation__grid-3__col u-size--w350px">
              <div className="c-grid__col">
                <InputInterest errorMessage={interestError} />
              </div>
            </div>
          </div>
          <div className="c-note">
            <p>取り崩し方法</p>
            <ul className="add-style">
              <li>定率：保有資産を一定の割合で取り崩す方法です。（例：年6%の場合、隔月に1%）</li>
              <li>
                定額：保有資産を一定の金額で取り崩す方法です。（例：毎月12万円の場合、年間144万円）
              </li>
            </ul>
          </div>
          <FinancialCalcReversalCalcButton isPending={isPending} isResponsive={isResponsive} />
        </form>
      </React.Fragment>
    )
  }
)
